import React, { useState, useEffect } from 'react';
import { apiService } from '../api';
import { CreateGameRequest, CreateCurrencySetting, Integrator, CurrencyCode, Game } from '../types';
import LoadingSpinner from './LoadingSpinner';

interface CreateGamePageProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const CreateGamePage: React.FC<CreateGamePageProps> = ({ onBack, onSuccess }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Form fields
  const [name, setName] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [openUrl, setOpenUrl] = useState<string>('');
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [currencySettings, setCurrencySettings] = useState<CreateCurrencySetting[]>([]);
  
  // Available integrators
  const [integrators, setIntegrators] = useState<Integrator[]>([]);
  const [loadingIntegrators, setLoadingIntegrators] = useState<boolean>(true);

  // Games list for "load from game"
  const [games, setGames] = useState<Game[]>([]);
  const [loadingGames, setLoadingGames] = useState<boolean>(true);
  const [selectedGameIdForLoad, setSelectedGameIdForLoad] = useState<string>('');
  const [loadingLoadSettings, setLoadingLoadSettings] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  // Valid currencies
  const validCurrencies: CurrencyCode[] = ['USD', 'EUR', 'BRL', 'MXN', 'ARS'];

  // Load integrators on mount
  useEffect(() => {
    const loadIntegrators = async () => {
      try {
        setLoadingIntegrators(true);
        const response = await apiService.getIntegrators();
        if (response.success) {
          setIntegrators(response.integrators);
        } else {
          setError(response.error || 'Failed to load integrators');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load integrators');
        console.error('Integrators load error:', err);
      } finally {
        setLoadingIntegrators(false);
      }
    };
    loadIntegrators();
  }, []);

  // Load games for "load from game" dropdown
  useEffect(() => {
    const loadGames = async () => {
      try {
        setLoadingGames(true);
        const response = await apiService.getGames();
        if (response.success && response.games) {
          setGames(response.games);
        }
      } catch (err) {
        console.error('Games load error:', err);
      } finally {
        setLoadingGames(false);
      }
    };
    loadGames();
  }, []);

  const addCurrencySetting = () => {
    setCurrencySettings([
      ...currencySettings,
      {
        integrator_id: integrators.length > 0 ? integrators[0].id : '',
        currency: 'USD',
        bet_levels: [],
        default_bet: 0,
        max_win: 0,
        max_win_multiplier: 0,
      },
    ]);
  };

  const removeCurrencySetting = (index: number) => {
    setCurrencySettings(currencySettings.filter((_, i) => i !== index));
  };

  const updateCurrencySetting = (index: number, field: keyof CreateCurrencySetting, value: any) => {
    const updated = [...currencySettings];
    updated[index] = { ...updated[index], [field]: value };
    setCurrencySettings(updated);
  };

  const updateBetLevels = (index: number, value: string) => {
    const levels = value
      .split(',')
      .map(v => parseInt(v.trim(), 10))
      .filter(v => !isNaN(v));
    updateCurrencySetting(index, 'bet_levels', levels);
  };

  const handleLoadSettingsFromGame = async () => {
    if (!selectedGameIdForLoad) return;
    const gameId = parseInt(selectedGameIdForLoad, 10);
    if (isNaN(gameId)) return;
    setError('');
    setLoadingLoadSettings(true);
    try {
      const settings = await apiService.getCurrencySettings(gameId);
      const normalized: CreateCurrencySetting[] = (settings || []).map(s => ({
        integrator_id: s.integrator_id ?? '',
        currency: s.currency ?? 'USD',
        bet_levels: Array.isArray(s.bet_levels) ? s.bet_levels : [],
        default_bet: typeof s.default_bet === 'number' ? s.default_bet : 0,
        max_win: typeof s.max_win === 'number' ? s.max_win : 0,
        max_win_multiplier: typeof s.max_win_multiplier === 'number' ? s.max_win_multiplier : 0,
      }));
      setCurrencySettings(normalized);
      setSuccess(normalized.length ? `Loaded ${normalized.length} currency setting(s) from game.` : 'No currency settings found for this game.');
    } catch (err: any) {
      const msg = err.response?.data?.error ?? err.message ?? 'Failed to load currency settings';
      setError(msg);
    } finally {
      setLoadingLoadSettings(false);
    }
  };

  const clearAllCurrencySettings = () => {
    setCurrencySettings([]);
    setSelectedGameIdForLoad('');
    setSuccess('');
  };

  const validateForm = (): boolean => {
    setError('');
    if (!name || !imageUrl || !description || !openUrl) {
      setError('Please fill in all required fields');
      return false;
    }
    if (currencySettings.length === 0) {
      setError('Please add at least one currency setting');
      return false;
    }
    for (let i = 0; i < currencySettings.length; i++) {
      const setting = currencySettings[i];
      if (!setting.integrator_id) {
        setError(`Currency setting ${i + 1}: please select an integrator`);
        return false;
      }
      if (!setting.currency) {
        setError(`Currency setting ${i + 1}: currency is required`);
        return false;
      }
      if (setting.bet_levels.length === 0) {
        setError(`Currency setting ${i + 1}: bet levels are required`);
        return false;
      }
      if (setting.default_bet <= 0) {
        setError(`Currency setting ${i + 1}: default bet must be greater than 0`);
        return false;
      }
      if (setting.max_win <= 0) {
        setError(`Currency setting ${i + 1}: max win must be greater than 0`);
        return false;
      }
      if (setting.max_win_multiplier <= 0) {
        setError(`Currency setting ${i + 1}: max win multiplier must be greater than 0`);
        return false;
      }
    }
    return true;
  };

  const doCreateGame = async () => {
    setError('');
    setSuccess('');
    try {
      setLoading(true);
      const request: CreateGameRequest = {
        name,
        image_url: imageUrl,
        description,
        open_url: openUrl,
        base_url: baseUrl,
        currency_settings: currencySettings,
      };
      const response = await apiService.createGame(request);
      if (response.success) {
        setSuccess(`Game "${response.name}" created successfully with ID: ${response.id}`);
        setName('');
        setImageUrl('');
        setDescription('');
        setOpenUrl('');
        setBaseUrl('');
        setCurrencySettings([]);
        if (onSuccess) {
          setTimeout(() => onSuccess(), 2000);
        }
      } else {
        setError(response.error || 'Failed to create game');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create game');
      console.error('Create game error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmYes = () => {
    setShowConfirmDialog(false);
    doCreateGame();
  };

  const handleConfirmNo = () => {
    setShowConfirmDialog(false);
  };

  if (loadingIntegrators) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Create New Game</h1>
        <button onClick={onBack} className="btn btn-logout" style={{ padding: '8px 16px' }}>
          ← Back
        </button>
      </div>

      {error && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fee', 
          color: '#c33', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#efe', 
          color: '#3c3', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}>
        {/* Basic Game Information */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ marginBottom: '15px', color: '#333' }}>Basic Information</h2>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333' }}>
              Game Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                maxWidth: '500px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333' }}>
              Image URL *
            </label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                maxWidth: '500px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333' }}>
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                maxWidth: '500px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333' }}>
              Open URL *
            </label>
            <input
              type="url"
              value={openUrl}
              onChange={(e) => setOpenUrl(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                maxWidth: '500px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333' }}>
              Base URL
            </label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                maxWidth: '500px'
              }}
            />
          </div>
        </div>

        {/* Currency Settings */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: 0, marginBottom: '15px', color: '#333' }}>Currency Settings</h2>

          {/* Load from existing game */}
          <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #ddd' }}>
            <div style={{ marginBottom: '10px', fontWeight: '500', color: '#333', fontSize: '14px' }}>Load from existing game</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
              <select
                value={selectedGameIdForLoad}
                onChange={(e) => setSelectedGameIdForLoad(e.target.value)}
                disabled={loadingGames}
                style={{
                  minWidth: '280px',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: loadingGames ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">Select game to copy settings from</option>
                {games.map(game => (
                  <option key={game.id} value={String(game.id)}>
                    {game.name} (ID: {game.id})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleLoadSettingsFromGame}
                disabled={!selectedGameIdForLoad || loadingLoadSettings || loadingGames}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (selectedGameIdForLoad && !loadingLoadSettings && !loadingGames) ? '#2196F3' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (selectedGameIdForLoad && !loadingLoadSettings && !loadingGames) ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {loadingLoadSettings ? 'Loading...' : 'Load settings from game'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={addCurrencySetting}
                disabled={integrators.length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: integrators.length > 0 ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: integrators.length > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                + Add Currency Setting
              </button>
              <button
                type="button"
                onClick={clearAllCurrencySettings}
                disabled={currencySettings.length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: currencySettings.length > 0 ? '#ff9800' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currencySettings.length > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Clear all
              </button>
            </div>
          </div>

          {currencySettings.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666', backgroundColor: 'white', borderRadius: '4px' }}>
              No currency settings added. Click "Add Currency Setting" to add one.
            </div>
          ) : (
            currencySettings.map((setting, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  border: '1px solid #ddd',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Currency Setting {index + 1}</h3>
                  <button
                    type="button"
                    onClick={() => removeCurrencySetting(index)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333', fontSize: '14px' }}>
                      Integrator *
                    </label>
                    <select
                      value={setting.integrator_id}
                      onChange={(e) => updateCurrencySetting(index, 'integrator_id', e.target.value)}
                      required
                      style={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      {integrators.length === 0 ? (
                        <option value="">No integrators available</option>
                      ) : (
                        <>
                          <option value="">Select integrator</option>
                          {integrators.map(integrator => (
                            <option key={integrator.id} value={integrator.id}>
                              {integrator.name} ({integrator.id})
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333', fontSize: '14px' }}>
                      Currency *
                    </label>
                    <select
                      value={setting.currency}
                      onChange={(e) => updateCurrencySetting(index, 'currency', e.target.value)}
                      required
                      style={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      {validCurrencies.map(currency => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333', fontSize: '14px' }}>
                      Bet Levels * (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={setting.bet_levels.join(', ')}
                      onChange={(e) => updateBetLevels(index, e.target.value)}
                      placeholder="10, 20, 50, 100"
                      required
                      style={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333', fontSize: '14px' }}>
                      Default Bet * (cents)
                    </label>
                    <input
                      type="number"
                      value={setting.default_bet || ''}
                      onChange={(e) => updateCurrencySetting(index, 'default_bet', parseInt(e.target.value, 10) || 0)}
                      required
                      min="1"
                      style={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333', fontSize: '14px' }}>
                      Max Win * (cents)
                    </label>
                    <input
                      type="number"
                      value={setting.max_win || ''}
                      onChange={(e) => updateCurrencySetting(index, 'max_win', parseInt(e.target.value, 10) || 0)}
                      required
                      min="1"
                      style={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#333', fontSize: '14px' }}>
                      Max Win Multiplier *
                    </label>
                    <input
                      type="number"
                      value={setting.max_win_multiplier || ''}
                      onChange={(e) => updateCurrencySetting(index, 'max_win_multiplier', parseInt(e.target.value, 10) || 0)}
                      required
                      min="1"
                      style={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Submit Button */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: '12px 24px',
              backgroundColor: '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || currencySettings.length === 0}
            style={{
              padding: '12px 24px',
              backgroundColor: (loading || currencySettings.length === 0) ? '#ccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (loading || currencySettings.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {loading ? 'Creating...' : 'Create Game'}
          </button>
        </div>
      </form>

      {/* Confirmation dialog */}
      {showConfirmDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={handleConfirmNo}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ margin: '0 0 20px', fontSize: '16px', color: '#333' }}>
              Are you sure you want to create a new game?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleConfirmNo}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ccc',
                  color: '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                No
              </button>
              <button
                type="button"
                onClick={handleConfirmYes}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateGamePage;
